import {
  bisector,
  curveCardinal,
  format as d3Format,
  extent,
  group,
  line,
  scaleLinear,
  scaleOrdinal,
  scaleUtc,
} from 'd3';
import { clamp, isDate, isNumber } from 'lodash';
import { useRef, useState } from 'react';

import { ReplotChartNames } from '../../constants';
import { type CommonPlotProps, FormatType } from '../../types';
import {
  getFormatter,
  getMaxTextWidth,
  getScaleTicks,
  useRenderCheck,
  useResizeObserver,
  xAxisTickCount,
  yAxisTickCount,
} from '../../utilities';
import {
  type DateFormat,
  formatDate,
  shortMonth,
} from '../../utilities/date-formatter';
import {
  getOtherColor,
  getPrimaryColor,
  plotColors,
} from '../../utilities/plot-colors';
import { plotTestId } from '../../utilities/plotTestId';
import { tickAnchor } from '../../utilities/tick-anchor';
import { AXIS_NO_WRAP_HEIGHT, AxisLabel } from '../axis/axis-label';
import { Legend } from '../legend/legend';
import { PlotLoadWrapper } from '../plot-loader/plot-loader';
import {
  PlotTooltip,
  TooltipHoverLine,
  type TooltipPosition,
  useTooltipController,
} from '../plot-tooltip';
import styles from './line-chart.module.css';
import { LineTooltip, type LineTooltipProps } from './line-tooltip';
import { dedupeDataDimLabels, formatDataDates, getLineDiffText } from './utils';

type LineDatum<TDateFormat = string> = {
  date: TDateFormat;
  value: number | null;
  secondaryValue?: number | null;
};

export type LineData<TDateFormat = string> = {
  label: string;
  values: LineDatum<TDateFormat>[];
  color?: string;
  strokeWidth?: number;
};

type AxisOptions = {
  min: number;
  max: number;
};

type LineChartProps = {
  data: LineData[];
  colors?: string[];
  showGridLines?: boolean;
  yAxis?: AxisOptions;
  dateFormat?: DateFormat;
  showDiffLabel?: boolean;
  showLegend?: boolean;
  curveTension?: number;
  fontSize?: number;
  tooltipFormat?: FormatType;
} & CommonPlotProps;

const Y_AXIS_MAX_WIDTH = 50;
const X_AXIS_HEIGHT = 12;
const LEGEND_HEIGHT = 24;
const LEGEND_TOP_OFFSET = 6;

const CHART_PADDING_LEFT = 6;
const CHART_PADDING_TOP = 8;
const CHART_PADDING_BOTTOM = 8;

const bisect = bisector<Date, Date>((d) => d).center;

const RIGHT_LABEL_OFFSET_X = 8;

const DEFAULT_LINE_STROKE_WIDTH = 2;

export const LineChart = ({
  data,
  yAxis,
  format = FormatType.SI,
  tooltipFormat,
  currencyCode = 'USD',
  dateFormat = 'day',
  showGridLines = true,
  loading = false,
  colors,
  renderUpdate,
  showDiffLabel = false,
  showLegend = false,
  curveTension = 0,
  fontSize = 11,
}: LineChartProps) => {
  const { containerRef, width, height } = useResizeObserver();

  const xAxisHeight = X_AXIS_HEIGHT;
  const legendHeight = showLegend ? LEGEND_HEIGHT + LEGEND_TOP_OFFSET : 0;

  const chartHeight = Math.max(0, height - legendHeight);
  const plotHeight = Math.max(
    0,
    chartHeight - CHART_PADDING_TOP - CHART_PADDING_BOTTOM - xAxisHeight
  );

  const dataFormattedDates = formatDataDates(data);
  const dataDedupedLabels = dedupeDataDimLabels(dataFormattedDates);

  /** ================================
   * Color Scale
   ================================ */
  const colorScale = scaleOrdinal<string>()
    .domain(dataDedupedLabels.map((d) => d.label))
    .range(
      dataDedupedLabels.map(
        (d, i) => d.color || colors?.[i] || plotColors[i] || getOtherColor()
      )
    );

  /** ================================
   * Y Axis
   ================================ */
  const allValues = dataDedupedLabels
    .flatMap((d) => d.values.map((v) => v.value))
    .filter((v) => v !== null);
  const domain = yAxis ? [yAxis.min, yAxis.max] : extent(allValues);
  const yScaleTickCount = yAxisTickCount(plotHeight);

  const yScale =
    isNumber(domain[0]) && isNumber(domain[1])
      ? scaleLinear()
          .domain([domain[0], domain[1]])
          .nice(yScaleTickCount)
          .range([plotHeight, 0])
      : null;

  const yTicks = (() => {
    if (!yScale) return [];

    const formatTick = getFormatter(format, {
      values: yScale.ticks(yScaleTickCount),
      currencyCode,
    });

    const yScaleTicks = yScale
      .ticks(yScaleTickCount)
      .filter((tick) =>
        format === FormatType.INTEGER ? Number.isInteger(tick) : true
      )
      .map((tick) => ({ value: tick, label: formatTick(tick) }));

    if (yScaleTickCount <= 3 && yScaleTicks.length > yScaleTickCount) {
      return getScaleTicks({
        domain: yScale.domain(),
        tickCount: yScaleTickCount,
        format: format,
        currencyCode,
      });
    }

    return yScaleTicks;
  })();

  const yAxisWidth = yTicks
    ? Math.min(
        getMaxTextWidth({ texts: yTicks?.map((t) => t.label), fontSize }),
        Y_AXIS_MAX_WIDTH
      )
    : Y_AXIS_MAX_WIDTH;

  const {
    firstDiffValue,
    secondDiffValue,
    text: diffText,
    rightGutter: diffTextRightGutter,
  } = getLineDiffText({
    showDiffLabel,
    data: dataDedupedLabels,
    format,
  });

  const plotWidth =
    width - yAxisWidth - CHART_PADDING_LEFT - diffTextRightGutter;

  const isPlotSizeValid = plotWidth > 0 && height > 0;

  const legendLabels = dataDedupedLabels.map((d) => d.label);

  /** ================================
   * X Axis
   ================================ */
  const groupedData = group(
    dataDedupedLabels.flatMap((d) =>
      d.values.map((v) => ({ ...v, label: d.label }))
    ),
    (d) => d.date
  );

  const allDates = Array.from(groupedData.keys()).sort(
    (a, b) => a.getTime() - b.getTime()
  );
  const dateDomain = extent(allDates);

  const xScaleTickCount = xAxisTickCount(plotWidth);
  const xScale =
    isDate(dateDomain[0]) && isDate(dateDomain[1])
      ? scaleUtc().domain([dateDomain[0], dateDomain[1]]).range([0, plotWidth])
      : null;

  const xTicks = xScale?.ticks(xScaleTickCount).map((tick) => ({
    value: tick,
    label: shortMonth(xScale?.tickFormat()(tick)),
  }));

  /** ================================
   * Line Path
   ================================ */
  const lineGenerator =
    xScale && yScale
      ? line<LineDatum<Date>>()
          .x((d) => xScale(d.date))
          .y((d) => yScale(d.value || 0))
          .defined((d) => d.value !== null)
          .curve(curveCardinal.tension(clamp(curveTension, 0, 1)))
      : null;

  const lines = dataDedupedLabels
    .map((d) => {
      if (!lineGenerator) return null;

      const path = lineGenerator(d.values);
      if (!path) return null;

      return { ...d, path };
    })
    .filter((d) => !!d)
    .map((d) => ({ ...d, color: colorScale(d.label) }));

  /** ================================
   * Tooltip
   ================================ */
  const tooltipFormatType = (() => {
    if (tooltipFormat) return tooltipFormat;
    if (format === FormatType.SI) return FormatType.INTEGER;
    if (format === FormatType.CURRENCY) {
      return FormatType.CURRENCY_INTEGER;
    }
    return format;
  })();

  const tooltipFormatValue = getFormatter(tooltipFormatType, {
    values: allValues,
    currencyCode,
  });

  const [tooltipRows, setTooltipRows] = useState<LineTooltipProps | null>(null);
  const [tooltipPosition, setTooltipPosition] =
    useState<TooltipPosition | null>(null);

  const handleTooltipMove = (position: [number, number]) => {
    if (!xScale) return;

    const date = xScale.invert(position[0]);
    const index = bisect(allDates, date);
    const bisectDate = allDates[index];

    if (!bisectDate) return;

    const dataPoints = groupedData.get(bisectDate);
    if (!dataPoints) return;

    const title = formatDate(bisectDate, dateFormat);

    setTooltipRows({
      title,
      rows: dataPoints
        .map((d) => ({
          ...{ color: colorScale(d.label) || getPrimaryColor() },
          label: d.label,
          value:
            typeof d.value === 'number'
              ? `${typeof d.secondaryValue === 'number' ? `${d3Format(',.0f')(d.secondaryValue)} / ` : ''}${tooltipFormatValue(d.value)}`
              : '-',
          sortValue: d.value || 0,
        }))
        .sort((a, b) => b.sortValue - a.sortValue)
        .map(({ sortValue: _sortValue, ...row }) => row),
    });

    const maxValue = Math.max(
      ...dataPoints.map((d) => d.value).filter((v) => v !== null)
    );

    const y =
      Number.isFinite(maxValue) && yScale ? yScale(maxValue) : plotHeight / 2;

    setTooltipPosition({ x: position[0], y });
  };

  const tooltipController = useTooltipController({
    onHover: handleTooltipMove,
  });

  const chartRef = useRef<SVGGElement>(null);
  useRenderCheck(chartRef, { renderUpdate });

  // Need at least 2 data points to draw a line
  const hasData =
    data.length &&
    data.some((d) => d.values.filter((v) => isNumber(v.value)).length >= 2);

  return (
    <div
      ref={containerRef}
      className={styles['container']}
      data-testid={plotTestId(ReplotChartNames.LineChart)}
    >
      <PlotLoadWrapper loading={loading} noData={!hasData}>
        <svg width="100%" height={chartHeight}>
          {isPlotSizeValid && (
            <g transform={`translate(0, ${CHART_PADDING_TOP})`}>
              <g
                id="y-axis"
                data-testid="plot-y-axis"
                className={styles['yAxis']}
              >
                {yScale &&
                  yTicks?.map((tick, i) => (
                    <AxisLabel
                      key={i}
                      y={yScale(tick.value)}
                      width={yAxisWidth}
                      availableHeight={AXIS_NO_WRAP_HEIGHT}
                      label={tick.label}
                      {...(fontSize && { fontSize })}
                    />
                  ))}
              </g>
              <g
                id="x-axis"
                data-testid="plot-x-axis"
                transform={`translate(${yAxisWidth + CHART_PADDING_LEFT}, ${plotHeight + CHART_PADDING_BOTTOM})`}
                className={styles['xAxis']}
              >
                {xScale &&
                  xTicks?.map((tick, i) => (
                    <text
                      key={i}
                      transform={`translate(${xScale(tick.value)}, 0)`}
                      y={9}
                      {...(fontSize && { fontSize })}
                      style={{
                        textAnchor: tickAnchor(
                          i,
                          xTicks.length,
                          xScale(tick.value),
                          plotWidth
                        ),
                      }}
                    >
                      {tick.label}
                    </text>
                  ))}
              </g>
              <g
                id="chart"
                ref={chartRef}
                transform={`translate(${yAxisWidth + CHART_PADDING_LEFT}, 0)`}
                {...tooltipController.gProps}
                className={styles.chart}
              >
                {showGridLines && (
                  <g id="axis-grid">
                    {yScale &&
                      yTicks?.map((tick, i) => (
                        <line
                          key={i}
                          x1={0}
                          x2={plotWidth}
                          y1={yScale(tick.value)}
                          y2={yScale(tick.value)}
                          className={styles['axisGridLine']}
                        />
                      ))}
                  </g>
                )}
                {lines.map((line, i) => {
                  const strokeWidth =
                    line.strokeWidth ?? DEFAULT_LINE_STROKE_WIDTH;

                  return (
                    <path
                      key={i}
                      d={line.path}
                      stroke={line.color}
                      strokeWidth={strokeWidth}
                      className={`line-path ${styles['line-path']}`}
                      data-testid="line-chart__line"
                      data-zero-data={line.values.every(
                        (v) => v.value === 0 || v.value === null
                      )}
                    />
                  );
                })}
                {showDiffLabel &&
                  diffText &&
                  yScale &&
                  firstDiffValue !== null &&
                  secondDiffValue !== null && (
                    <text
                      x={plotWidth + RIGHT_LABEL_OFFSET_X}
                      y={(yScale(firstDiffValue) + yScale(secondDiffValue)) / 2}
                      className={styles.diffLabel}
                      dominantBaseline="middle"
                      data-testid="line-chart__diff-label"
                      pointerEvents="none"
                    >
                      {diffText}
                    </text>
                  )}
                {tooltipController.isVisible && tooltipPosition && (
                  <TooltipHoverLine x={tooltipPosition.x} height={plotHeight} />
                )}
                <rect
                  x={0}
                  y={0}
                  width={plotWidth}
                  height={plotHeight}
                  fill="transparent"
                />
              </g>
            </g>
          )}
        </svg>
        {showLegend && (
          <div
            className={styles.legendContainer}
            style={{
              marginLeft: `${yAxisWidth + CHART_PADDING_LEFT}px`,
              maxHeight: LEGEND_HEIGHT,
            }}
          >
            <Legend
              items={legendLabels.map((label) => ({
                label,
                color: colorScale(label),
              }))}
              marker="line"
            />
          </div>
        )}
        {tooltipPosition && (
          <PlotTooltip
            isVisible={tooltipController.isVisible}
            x={tooltipPosition.x + yAxisWidth}
            y={tooltipPosition.y}
          >
            {tooltipRows && (
              <LineTooltip title={tooltipRows.title} rows={tooltipRows.rows} />
            )}
          </PlotTooltip>
        )}
      </PlotLoadWrapper>
    </div>
  );
};
